import * as Popover from "@radix-ui/react-popover";
import { Editor } from "@tiptap/core";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { cn } from "src/lib/cn";
import IconButton from "src/components/IconButton";
import { keepFocusInEditorIfEventFromEditor } from "src/components/TiptapDocumentSB/helpers";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { LinkEditorCard } from "./LinkEditorCard";
import { normalizeLinkHref, trimRangeWhitespace } from "./linkUtils";

interface LinkPopoverProps {
  editor: Editor | null;
  className?: string;
}

export const LinkPopover = ({ editor, className }: LinkPopoverProps) => {
  const [open, setOpen] = useState(false);
  // Save selection when popover opens so we can restore it after applying the
  // link — focusing the URL input blurs the editor and drops its selection.
  const [savedSelection, setSavedSelection] = useState<{ from: number; to: number } | null>(null);
  const [hasExistingLink, setHasExistingLink] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track when we're programmatically focusing the editor (to ignore the resulting click events)
  const isApplyingRef = useRef(false);

  const isLinkActive = !!editor?.isActive("link");
  // Nothing to link: no text selected and the caret isn't on an existing link
  const isDisabled = !editor || (editor.state.selection.empty && !isLinkActive);

  // Close popover when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't close if clicking inside the popover content or trigger (identified by data attribute)
      if (target.closest("[data-link-popover]")) {
        return;
      }

      // Ignore editor clicks that result from our programmatic focus
      const container = document.getElementById("editorContainer");
      if (isApplyingRef.current && container && target instanceof Node && container.contains(target)) {
        return;
      }

      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Focus the URL input once the popover content mounts (Radix autofocus is
  // prevented — with modal={false} it isn't reliable). Select-all so a
  // prefilled href is replaceable in one keystroke.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!editor || isDisabled) return;

    // Trim edge whitespace so selecting ` some link ` edits the existing link
    // (isActive is coverage-based, and the spaces carry no link mark).
    const { from, to } = editor.state.selection;
    const trimmed = trimRangeWhitespace(editor.state.doc, { from, to });
    if (trimmed.from !== from || trimmed.to !== to) {
      editor.commands.setTextSelection(trimmed);
    }

    setSavedSelection(trimmed);
    setHasExistingLink(!!editor.isActive("link"));
    setUrlValue(editor.getAttributes("link")?.href ?? "");
    setError(false);
    setOpen(true);
  };

  const runWithSelection = (buildChain: (chain: ReturnType<Editor["chain"]>) => { run: () => boolean }): boolean => {
    if (!editor || !savedSelection) return false;

    isApplyingRef.current = true;
    // A caret targets the whole link (extendMarkRange expands any selection
    // sitting fully inside the mark, so it must not run for range selections);
    // an explicit selection is honored as-is, so selecting part of a link
    // relinks/unlinks only the selected text.
    const selectionChain = editor.chain().focus().setTextSelection(savedSelection);
    const result = buildChain(savedSelection.from === savedSelection.to ? selectionChain.extendMarkRange("link") : selectionChain).run();

    // Reset the flag after a short delay
    setTimeout(() => {
      isApplyingRef.current = false;
    }, 100);

    return result;
  };

  const applyLink = () => {
    const href = normalizeLinkHref(urlValue);
    if (!href) {
      setError(true);
      return;
    }

    if (runWithSelection((chain) => chain.setLink({ href }))) {
      setOpen(false);
    } else {
      setError(true);
    }
  };

  const removeLink = () => {
    runWithSelection((chain) => chain.unsetLink());
    setOpen(false);
  };

  const closeAndRefocusEditor = () => {
    setOpen(false);
    editor?.commands.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      applyLink();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndRefocusEditor();
    }
  };

  return (
    // Non-modal for the same reason as HighlightColorPicker: a modal
    // popover's focus trap would fight the editor.chain().focus() we run
    // on apply/remove. Outside clicks close via our document mousedown
    // listener.
    <Popover.Root open={open} modal={false}>
      {/* IconButton renders its own tooltip wrapper, so this separate anchor
          gives Radix a DOM node to measure without changing the button. */}
      <Popover.Anchor asChild>
        <span data-link-popover className="inline-flex">
          {/* The wrapper remains hoverable when IconButton is disabled. */}
          <SudoTooltip
            text={isDisabled ? "Select text to add a link" : "Link"}
            disabled={open}
            hasArrow={false}
            triggerClassName="max-w-[180px]"
            position="bottom"
          >
            <IconButton
              data-link-popover
              src="/assets/editor-toolbar/link.svg"
              ariaLabel="Insert link"
              disableTooltip
              disabled={isDisabled}
              className={classNames("toolbar-icon", { active: isLinkActive }, className)}
              aria-expanded={open}
              aria-haspopup="dialog"
              onClick={toggleOpen}
            />
          </SudoTooltip>
        </span>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          data-link-popover
          side="bottom"
          align="center"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={keepFocusInEditorIfEventFromEditor}
          onEscapeKeyDown={closeAndRefocusEditor}
          className={cn("z-[100] origin-top animate-flyout")}
        >
          <LinkEditorCard
            mode="edit"
            url={urlValue}
            error={error}
            showRemove={hasExistingLink}
            inputRef={inputRef}
            onUrlChange={(value) => {
              setUrlValue(value);
              setError(false);
            }}
            onInputKeyDown={handleInputKeyDown}
            onApply={applyLink}
            onRemove={removeLink}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
